import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { api } from "@/lib/api";
import { storeApi } from "@/lib/storeApi";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "botgenomix_admin_token";

/**
 * Owner-only admin auth. Single Bearer token stored in localStorage so the
 * session survives page reloads / browser restarts ("remember me" default).
 * Attaches Authorization header globally via axios interceptor - onto the
 * global axios AND onto the two axios instances the app actually uses
 * (`api`, `storeApi`). Without the instance-level interceptor those calls
 * skip the header and hit 401 (v6.5.26 fix).
 */
export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [status, setStatus] = useState("loading"); // loading | anon | owner

  // Global axios interceptor: attach token on every request, clear on 401.
  useEffect(() => {
    const instances = [axios, api, storeApi];
    const reqIds = instances.map((inst) =>
      inst.interceptors.request.use((cfg) => {
        const t = localStorage.getItem(TOKEN_KEY);
        if (t) cfg.headers.Authorization = `Bearer ${t}`;
        return cfg;
      })
    );
    const resIds = instances.map((inst) =>
      inst.interceptors.response.use(
        (r) => r,
        (err) => {
          if (err?.response?.status === 401) {
            const url = err?.config?.url || "";
            // Don't nuke session on public endpoints (store/help/downloads).
            if (url.includes("/store/") || url.includes("/help/") || url.includes("/downloads/") || url.includes("/community/")) {
              return Promise.reject(err);
            }
            localStorage.removeItem(TOKEN_KEY);
            setToken(null);
            setStatus("anon");
          }
          return Promise.reject(err);
        }
      )
    );
    return () => {
      instances.forEach((inst, i) => {
        inst.interceptors.request.eject(reqIds[i]);
        inst.interceptors.response.eject(resIds[i]);
      });
    };
  }, []);

  // Hydrate on mount: if we have a token, verify it against /me.
  useEffect(() => {
    let alive = true;
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setStatus("anon"); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => {
        if (!alive) return;
        if (r.data?.authenticated) {
          setToken(t);
          setStatus("owner");
        } else {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setStatus("anon");
        }
      })
      .catch(() => {
        if (!alive) return;
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setStatus("anon");
      });
    return () => { alive = false; };
  }, []);

  const login = async (password) => {
    const r = await axios.post(`${API}/auth/login`, { password, remember_me: true });
    const tk = r.data?.token;
    if (!tk) throw new Error("No token returned");
    localStorage.setItem(TOKEN_KEY, tk);
    setToken(tk);
    setStatus("owner");
    return true;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setStatus("anon");
  };

  const value = useMemo(() => ({ token, status, login, logout }), [token, status]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
